<script setup>
import ButtonBase from '@/Common/Elements/Buttons/ButtonBase.vue'
import CardBase from '@/Common/Layout/Containers/CardBase.vue'
import SpanText from '@/Common/Text/SpanText.vue'
import { secondaryDividerBorder } from '@/Common/util/tailwind/colorConstants'
import {
  CommandLineIcon,
  ExclamationCircleIcon,
} from '@heroicons/vue/24/outline'
import { computed, ref } from 'vue'
import { toolNameFromPart } from '../BidBoostLangGraphTransport'
import { ToolInput, ToolOutput } from '../Tool'
import ActivityDisclosure from './ActivityDisclosure.vue'
import ActivityList from './ActivityList.vue'
import {
  toolActivityLabel,
  toolActivityState,
  toolActivityTarget,
} from './activityLabels'

const props = defineProps({
  part: { type: Object, required: true },
  status: { type: String, default: 'complete' },
})

const open = ref(false)
const hasOpened = ref(false)
function toggleOpen() {
  hasOpened.value = true
  open.value = !open.value
}
const state = computed(() => toolActivityState(props.part, props.status))
const childStatus = computed(() => {
  if (['complete', 'error', 'stopped', 'waiting'].includes(state.value))
    return state.value
  return state.value === 'denied' ? 'stopped' : props.status
})
const label = computed(() => {
  const action = toolActivityLabel(props.part, props.status)
  const target = toolActivityTarget(props.part)
  return target
    ? `${action.replace(/…$/, '')} · ${target}${state.value === 'running' ? '…' : ''}`
    : action
})
</script>

<template>
  <div class="min-w-0" data-testid="activity-tool-call">
    <div class="flex min-w-0 items-center gap-2">
      <ActivityDisclosure
        :open="open"
        :running="state === 'running'"
        :label="label"
        :error="state === 'error'"
        :icon="state === 'error' ? ExclamationCircleIcon : CommandLineIcon"
        class="min-w-0"
        data-testid="activity-tool-disclosure"
        @toggle="toggleOpen"
      />
      <ButtonBase
        v-if="
          toolNameFromPart(part) === 'save_user_memory' && state === 'complete'
        "
        color="white"
        size="xs"
        shape="rounded"
        class="shrink-0"
        :href="route('agent.memory.edit')"
        >View memory</ButtonBase
      >
    </div>
    <div
      v-if="hasOpened"
      v-show="open"
      class="mt-1 mb-2 min-w-0 space-y-2"
      data-testid="activity-tool-details"
    >
      <ActivityList
        v-if="part.children?.length"
        :parts="part.children"
        :status="childStatus"
        class="ml-2 border-l pl-3"
        :class="secondaryDividerBorder"
      />
      <!-- Leave room for CardBase's outer ring inside the message's overflow clip. -->
      <CardBase class="mr-px min-w-0">
        <div class="px-4 pt-4">
          <SpanText color="text-gray-500" size="text-xs" class="break-all">{{
            toolNameFromPart(part)
          }}</SpanText>
        </div>
        <ToolInput v-if="part.input !== undefined" :input="part.input" />
        <ToolOutput :output="part.output" :error-text="part.errorText" />
        <div
          v-if="part.output === undefined && !part.errorText"
          class="px-4 pb-4"
        >
          <SpanText color="text-gray-500" size="text-xs">{{
            state === 'running'
              ? 'Waiting for a result…'
              : 'No result returned.'
          }}</SpanText>
        </div>
      </CardBase>
    </div>
  </div>
</template>
